import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Logo from "@/components/Logo";
import { Smartphone, Download, Share2, Plus, ArrowLeft, Printer, Copy, CheckCircle2, Apple } from "lucide-react";

// Detect the production URL — this becomes the QR code target so users get the LIVE app, not preview.
// Priority: env → runtime origin → hard-coded fallback.
function getAppUrl() {
  const preferred = process.env.REACT_APP_PUBLIC_URL || "";
  if (preferred) return preferred.replace(/\/+$/, "");
  if (typeof window !== "undefined" && window.location) {
    const host = window.location.hostname;
    // If we're on the preview URL, still send users to production so QR codes work forever.
    if (host.includes("preview.emergentagent.com") || host.includes("emergent.host")) {
      return "https://app.asap-roadrunner.com";
    }
    return `${window.location.protocol}//${window.location.host}`;
  }
  return "https://app.asap-roadrunner.com";
}

export default function GetTheApp() {
  const url = getAppUrl();
  const [copied, setCopied] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setInstallEvent(null);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const printQR = () => window.print();

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white" data-testid="get-app-page">
      <div className="no-print bg-asap-deep text-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center gap-3 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-asap-yellow"><ArrowLeft size={14}/> Home</Link>
          <button onClick={printQR} data-testid="print-qr-btn" className="inline-flex items-center gap-2 px-3 py-2 bg-asap-yellow text-asap-deep font-bold text-xs uppercase tracking-wider border-2 border-asap-yellow hover:bg-white transition">
            <Printer size={14}/> Print / Save Flyer
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-10 print:py-4">
        <header className="text-center border-b-4 border-asap-deep pb-6 print:pb-4">
          <div className="flex justify-center"><Logo size={88}/></div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-asap-orange font-bold mt-4">Get The App</div>
          <h1 className="font-display text-4xl sm:text-6xl uppercase text-asap-deep mt-1 leading-none">Rescue in Your Pocket</h1>
          <p className="text-slate-600 mt-3 text-sm max-w-lg mx-auto">Scan the code — install ASAP Roadside on your phone in under 30 seconds. No app store needed.</p>
        </header>

        <section className="grid md:grid-cols-2 gap-8 items-center">
          {/* QR Code — big and printable */}
          <div className="text-center" data-testid="qr-wrapper">
            <div className="inline-block p-6 bg-white border-4 border-asap-deep shadow-hard" data-testid="qr-box">
              <QRCodeSVG
                value={url}
                size={280}
                bgColor="#FFFFFF"
                fgColor="#061224"
                level="H"
                includeMargin={false}
                imageSettings={{
                  src: "https://customer-assets.emergentagent.com/job_identity-bridge-17/artifacts/085ztinp_1922.png",
                  height: 56,
                  width: 56,
                  excavate: true,
                }}
              />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-widest font-bold text-slate-500">Point Your Camera At This</div>
            <div className="mt-1 flex items-center justify-center gap-2 print:hidden">
              <code className="text-xs text-asap-deep bg-slate-100 px-2 py-1 border border-slate-300" data-testid="qr-url">{url}</code>
              <button onClick={copyLink} data-testid="copy-url-btn" className="inline-flex items-center gap-1 text-xs font-bold text-asap-deep hover:text-asap-orange">
                {copied ? <><CheckCircle2 size={12} className="text-emerald-600"/> Copied</> : <><Copy size={12}/> Copy</>}
              </button>
            </div>
          </div>

          <div className="space-y-6 print:hidden">
            {installEvent && !installed && (
              <button
                onClick={triggerInstall}
                data-testid="install-app-btn"
                className="w-full p-4 bg-asap-yellow text-asap-deep border-2 border-asap-deep shadow-hard hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-hard-sm transition-all font-display uppercase text-lg inline-flex items-center justify-center gap-3"
              >
                <Download size={22}/> Install on This Device
              </button>
            )}
            {installed && (
              <div className="p-4 bg-emerald-50 border-2 border-emerald-500 text-emerald-800 font-bold inline-flex items-center gap-2" data-testid="install-success">
                <CheckCircle2 size={20}/> Installed! Look for the ASAP icon on your home screen.
              </div>
            )}

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-asap-orange">3 ways to install</div>
              <InstallCard num="1" icon={Smartphone} title="Scan the QR" body="Open your phone camera and point at the code. Tap the notification that pops up — done."/>
              <InstallCard num="2" icon={Apple} title="iPhone / iPad (Safari)" body={<>Open <code className="text-xs">asap-roadrunner.com</code> in Safari, tap the <Share2 size={12} className="inline mx-1"/> Share icon, then <strong>"Add to Home Screen"</strong>.</>}/>
              <InstallCard num="3" icon={Plus} title="Android (Chrome)" body={<>Open <code className="text-xs">asap-roadrunner.com</code> in Chrome, tap the ⋮ menu, then <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong>.</>}/>
            </div>

            <div className="p-4 bg-slate-100 border-l-4 border-asap-orange text-xs text-slate-700">
              <strong>Why not the App Store?</strong> Store submission takes 2-3 weeks of review. This installs INSTANTLY, works offline, updates automatically, and looks identical to a native app on your home screen.
            </div>
          </div>
        </section>

        <section className="print:hidden text-center">
          <div className="text-xs uppercase tracking-[0.25em] font-bold text-asap-orange">Share the App</div>
          <p className="text-slate-600 text-sm mt-2 max-w-lg mx-auto">Post this QR on your business cards, flyers, tow-truck door decals, gas station bulletin boards, and Facebook business page.</p>
        </section>

        <div className="hidden print:block text-center text-xs text-slate-600 mt-8">
          <div className="font-display uppercase text-asap-deep text-lg">ASAP Roadside — Roadrunner Rescue</div>
          <div>24/7 Rescue · Call 831-747-7141 · Text 765-330-1951 · asaproadrunners@rs-asap.com</div>
          <div className="mt-2">Scan to install the app instantly on your phone. Get help in taps, not calls.</div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function InstallCard({ num, icon: Icon, title, body }) {
  return (
    <div className="flex gap-3 p-3 border-2 border-slate-200 bg-white">
      <div className="w-10 h-10 shrink-0 bg-asap-deep text-white font-display grid place-items-center">{num}</div>
      <div className="flex-1">
        <div className="font-bold text-asap-deep flex items-center gap-2"><Icon size={14} className="text-asap-orange"/> {title}</div>
        <div className="text-sm text-slate-600 mt-1">{body}</div>
      </div>
    </div>
  );
}
